import * as React from 'react';
import { observer } from 'mobx-react';
import { Button } from '@estee/elc-buttons';
import { RemoveIcon } from '@estee/elc-icons';
import { ISurveyFields } from '~interfaces/ISurvey';
import { VtoSurveyButtons } from './VtoSurveyButtons';
import { Body1, Heading5 } from '@estee/elc-typography';
import styled from 'styled-components';
import {
    VTO_SURVEY_DISCLAIMER,
    VTO_SURVEY_QUESTION1_TITLE,
    VTO_SURVEY_END_MESSAGE
} from '../../constants/VtoSurvey';
import { translate } from '@estee/elc-service';
import { classes } from '../../utils/Classes';

interface IVtoSurveyTranslations {
    surveyQuestionOneLabel: string;
    surveyQuestionOneDescriptionLeft: string;
    surveyQuestionOneDescriptionRight: string;
    surveyQuestionTwoLabel: string;
    surveyQuestionTwoPlaceholder: string;
    surveyCtaLabel: string;
    surveyDisclaimer: string;
    surveyEndLabel: string;
}

interface IVtoSurvey {
    submitSurveyData({ rating, comment }: ISurveyFields): void;
    closeSurvey(): void;
    translations?: IVtoSurveyTranslations;
    showThankMessage: boolean;
    setThankMessageStatus(status: boolean): void;
    surveyButtonIndex: number;
    setSurveyButtonIndex(index: number): void;
    handleSurveySubmit(submitData: ISurveyFields): void;
    surveyComment: string;
    setSurveyComment(surveyComment: string): void;
}

const VtoSurveyContainer = styled.div`
    background-color: white;
    border: 1px solid black;
`;

const vtoSurveyContainerClassNames = classes('vto-survey__container');
const vtoSurveyCloseButtonClassNames = classes('vto-survey__close-button');
const vtoSurveyQuestion1TitleClassNames = classes('vto-survey__question-1-title');
const vtoSurveyQuestion1RatingDescriptionContainerClassNames = classes(
    'vto-survey__question-1-rating-description-container'
);
const vtoSurveyQuestion1RatingDescriptionLeftClassNames = classes(
    'vto-survey__question-1-rating-description-left'
);
const vtoSurveyQuestion1RatingDescriptionRightClassNames = classes(
    'vto-survey__question-1-rating-description-right'
);
const vtoSurveyQuestion2TitleClassNames = classes('vto-survey__question-2-title');
const vtoSurveyCenterWrapperClassNames = classes('vto-survey__section-wrapper--center');
const vtoSurveyQuestion2CommentClassNames = classes('vto-survey__question-2-comment');
const vtoSurveySubmitButtonClassNames = classes('vto-survey__submit-button');
const vtoSurveyDisclaimerClassNames = classes('vto-survey__disclaimer');
const vtoSurveyEndMessageClassNames = classes('vto-survey__end-message');

export const VtoSurvey = translate([
    'surveyQuestionOneLabel',
    'surveyQuestionOneDescriptionLeft',
    'surveyQuestionOneDescriptionRight',
    'surveyQuestionTwoLabel',
    'surveyQuestionTwoPlaceholder',
    'surveyCtaLabel',
    'surveyDisclaimer',
    'surveyEndLabel'
])(
    observer(
        ({
            submitSurveyData,
            closeSurvey,
            translations,
            showThankMessage,
            setThankMessageStatus,
            surveyButtonIndex,
            setSurveyButtonIndex,
            handleSurveySubmit,
            surveyComment,
            setSurveyComment
        }: IVtoSurvey) => {
            const {
                surveyQuestionOneLabel,
                surveyQuestionOneDescriptionLeft,
                surveyQuestionOneDescriptionRight,
                surveyQuestionTwoLabel,
                surveyQuestionTwoPlaceholder,
                surveyCtaLabel,
                surveyDisclaimer,
                surveyEndLabel
            } = translations as IVtoSurveyTranslations;

            const handleSubmit = () => {
                const rating = surveyButtonIndex;
                const surveyData: ISurveyFields = {
                    rating,
                    comment: surveyComment
                };

                handleSurveySubmit(surveyData);
            };

            const handleCommentChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
                setSurveyComment(event.target.value);
            };

            return (
                <VtoSurveyContainer className={vtoSurveyContainerClassNames}>
                    <Button className={vtoSurveyCloseButtonClassNames} onClick={closeSurvey}>
                        <RemoveIcon />
                    </Button>
                    {!showThankMessage && (
                        <>
                            <Heading5
                                className={vtoSurveyQuestion1TitleClassNames}
                                data-test-id={VTO_SURVEY_QUESTION1_TITLE}
                            >
                                {surveyQuestionOneLabel}
                            </Heading5>

                            <VtoSurveyButtons
                                surveyButtonIndex={surveyButtonIndex}
                                setSurveyButtonIndex={setSurveyButtonIndex}
                            />

                            <div className={vtoSurveyQuestion1RatingDescriptionContainerClassNames}>
                                <Body1
                                    className={vtoSurveyQuestion1RatingDescriptionLeftClassNames}
                                >
                                    {surveyQuestionOneDescriptionLeft}
                                </Body1>
                                <Body1
                                    className={vtoSurveyQuestion1RatingDescriptionRightClassNames}
                                >
                                    {surveyQuestionOneDescriptionRight}
                                </Body1>
                            </div>

                            <Heading5 className={vtoSurveyQuestion2TitleClassNames}>
                                {surveyQuestionTwoLabel}
                            </Heading5>

                            <div className={vtoSurveyCenterWrapperClassNames}>
                                <textarea
                                    className={vtoSurveyQuestion2CommentClassNames}
                                    name="comment"
                                    value={surveyComment}
                                    onChange={handleCommentChange}
                                    placeholder={surveyQuestionTwoPlaceholder}
                                />
                            </div>
                            <div className={vtoSurveyCenterWrapperClassNames}>
                                <Button
                                    className={vtoSurveySubmitButtonClassNames}
                                    onClick={handleSubmit}
                                    disabled={!(surveyButtonIndex || surveyButtonIndex === 0)}
                                >
                                    {surveyCtaLabel}
                                </Button>
                            </div>

                            <Body1
                                className={vtoSurveyDisclaimerClassNames}
                                dangerouslySetInnerHTML={{
                                    __html: surveyDisclaimer
                                }}
                                data-test-id={VTO_SURVEY_DISCLAIMER}
                            />
                        </>
                    )}

                    {showThankMessage && (
                        <div className={vtoSurveyCenterWrapperClassNames}>
                            <Body1
                                className={vtoSurveyEndMessageClassNames}
                                data-test-id={VTO_SURVEY_END_MESSAGE}
                            >
                                {surveyEndLabel}
                            </Body1>
                        </div>
                    )}
                </VtoSurveyContainer>
            );
        }
    )
);
